import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { importCorporateEmployeesFromExcel } from '../../../../api/endpoints/corporateEmployee';
import type { ImportCorporateEmployeesResponse } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';

/** Excel'den toplu personel içe aktarma — CorporateEmployee/ImportCorporateEmployeesFromExcel
 *  (multipart, field name `file`, confirmed via curl). */
export function ExcelImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportCorporateEmployeesResponse | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (f: File) => importCorporateEmployeesFromExcel(f),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'list'] });
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    setFile(null);
    setResult(null);
    mutation.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Excel'den Personel İçe Aktar</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={mutation.isPending}>
            {file ? file.name : 'Excel Dosyası Seç'}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={(e) => {
                setResult(null);
                setFile(e.target.files?.[0] ?? null);
              }}
            />
          </Button>

          <ApiErrorAlert error={mutation.error} />

          {result && (
            <Box>
              <Alert severity={result.failedCount ? 'warning' : 'success'} sx={{ mb: 1 }}>
                {result.insertedCount ?? 0} personel eklendi, {result.failedCount ?? 0} kayıt başarısız oldu.
              </Alert>
              {!!result.warnings?.length && (
                <List dense>
                  {result.warnings.map((w, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText primary={w} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Dosya, sistemde tanımlı personel şablonuna uygun olmalıdır.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Kapat
        </Button>
        <Button
          onClick={() => file && mutation.mutate(file)}
          variant="contained"
          disabled={!file || mutation.isPending}
        >
          {mutation.isPending ? 'Yükleniyor…' : 'İçe Aktar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
